/**
 * The cycle on a date line: cycle bands, release/time-off spans, the dates
 * that set the scope, and a day ruler under them. Below 760 px it becomes a
 * plain list.
 */

import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { timelineLayout, timelineText, withCycleDay } from '../../lib/planning'
import type { PlanTimeline as Timeline } from '../../types/planning'

const MARK: Record<string, string> = {
  red: tokens.colorPaletteRedForeground1,
  amber: tokens.colorPaletteDarkOrangeForeground1,
  accent: tokens.colorBrandForeground1,
  today: tokens.colorNeutralForeground1,
}

const COOL = `color-mix(in srgb, ${tokens.colorBrandBackground} 40%, transparent)`
const GONE = `color-mix(in srgb, ${tokens.colorBrandBackground} 14%, transparent)`
const OFF = `repeating-linear-gradient(135deg, ${tokens.colorNeutralStroke1} 0 3px, transparent 3px 7px)`

const useStyles = makeStyles({
  wrap: {
    padding: '16px 16px 12px',
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  tl: {
    position: 'relative',
    height: '180px',
    '@media (max-width: 760px)': { display: 'none' },
  },
  track: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '66px',
    height: '34px',
    background: tokens.colorNeutralBackground3,
    borderRadius: '3px',
  },
  band: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    // At the band's start, not its middle: Today sits mid-cycle for most of
    // a cycle, and its dot landed on a centred label ("●Cycle 1").
    justifyContent: 'flex-start',
    paddingLeft: tokens.spacingHorizontalS,
    // 14 px, sentence case, for the projector (#1578).
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    borderRadius: '3px',
    color: tokens.colorNeutralForeground3,
  },
  // Above the release spans drawn after the bands, which used to cover it;
  // the band's own background stays underneath them.
  bandLabel: { position: 'relative', zIndex: 1 },
  current: {
    background: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    boxShadow: `inset 0 0 0 1.5px ${tokens.colorBrandStroke1}`,
  },
  span: { position: 'absolute', top: 0, bottom: 0, borderRadius: '2px' },
  gone: { position: 'absolute', top: 0, bottom: 0, background: GONE, borderRadius: '3px 0 0 3px' },
  axis: { position: 'absolute', left: 0, right: 0, top: '100px', height: '26px' },
  tick: {
    position: 'absolute',
    top: 0,
    width: 0,
    height: '4px',
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  monday: { height: '8px', borderLeftColor: tokens.colorNeutralStroke1 },
  // Above a deadline's line down to its label, which would cross the date
  // (Copilot on #1613).
  tickLabel: {
    position: 'absolute',
    zIndex: 1,
    top: '9px',
    padding: '0 2px',
    background: tokens.colorNeutralBackground1,
    transform: 'translateX(-50%)',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
  mk: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 0,
    '::before': {
      content: '""',
      position: 'absolute',
      left: '-1px',
      top: '54px',
      height: '50px',
      borderLeft: '2px solid currentColor',
    },
    '::after': {
      content: '""',
      position: 'absolute',
      left: '-6px',
      top: '78px',
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      background: 'currentColor',
      boxShadow: `0 0 0 3px ${tokens.colorNeutralBackground1}`,
    },
  },
  // A label below the ruler: the line reaches past it.
  mkDown: { '::before': { height: '74px' } },
  lab: {
    position: 'absolute',
    whiteSpace: 'nowrap',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    lineHeight: 1.3,
    color: tokens.colorNeutralForeground1,
  },
  up: { top: 0 },
  down: { top: '132px' },
  start: { transform: 'none', left: '-8px', alignItems: 'flex-start' },
  end: { transform: 'translateX(-100%)', left: '8px', alignItems: 'flex-end' },
  sub: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  days: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold },
  legend: {
    display: 'flex',
    gap: '18px',
    flexWrap: 'wrap',
    marginTop: '8px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    '@media (max-width: 760px)': { display: 'none' },
  },
  swatch: {
    display: 'inline-block',
    width: '18px',
    height: '12px',
    marginRight: '6px',
    verticalAlign: 'middle',
    borderRadius: '2px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  list: {
    display: 'none',
    margin: 0,
    paddingLeft: '20px',
    '@media (max-width: 760px)': { display: 'block' },
  },
  // Labels overlap on the chart, so the list shows on a wide screen too.
  listShown: { display: 'block', marginTop: '8px' },
})

export function PlanTimeline({ timeline }: { timeline: Timeline }) {
  const styles = useStyles()
  const shown = withCycleDay(timeline)
  const { bands, spans, markers, ticks, crowded } = timelineLayout(shown)
  const legend = [
    ['cool', 'Release + closeout', COOL],
    ['off', 'Holiday or time off', OFF],
  ].filter(([k]) => spans.some((s) => s.kind === k))
  const lines = timelineText(shown)

  return (
    <div className={styles.wrap}>
      <div className={styles.tl} role="img" aria-label={`Timeline. ${lines.join('; ')}.`}>
        <div className={styles.track}>
          {bands.map((b) => (
            <div
              key={b.label}
              className={mergeClasses(styles.band, b.current && styles.current)}
              style={{ left: `${b.left}%`, width: `${b.width}%` }}
            >
              <span className={styles.bandLabel}>{b.label}</span>
            </div>
          ))}
          {bands
            .filter((b) => b.elapsed > 0)
            .map((b) => (
              <div
                key={`gone-${b.label}`}
                className={styles.gone}
                data-testid="timeline-gone"
                style={{ left: `${b.left}%`, width: `${b.elapsed}%` }}
              />
            ))}
          {spans.map((s) => (
            <div
              key={`${s.kind}-${s.start}`}
              className={styles.span}
              title={s.label}
              style={{
                left: `${s.left}%`,
                width: `${s.width}%`,
                background: s.kind === 'off' ? OFF : COOL,
              }}
            />
          ))}
        </div>
        <div className={styles.axis} aria-hidden="true">
          {ticks.map((k) => (
            <span
              key={k.at}
              className={mergeClasses(styles.tick, k.label && styles.monday)}
              style={{ left: `${k.x}%` }}
            >
              {k.label && <span className={styles.tickLabel}>{k.label}</span>}
            </span>
          ))}
        </div>
        {markers.map((m) => (
          <div
            key={`${m.date}-${m.title}-${m.sub ?? ''}`}
            className={mergeClasses(styles.mk, m.side === 'down' && styles.mkDown)}
            style={{ left: `${m.x}%`, color: MARK[m.kind ?? ''] ?? tokens.colorNeutralForeground1 }}
          >
            <div
              className={mergeClasses(
                styles.lab,
                m.side === 'up' ? styles.up : styles.down,
                m.edge === 'start' && styles.start,
                m.edge === 'end' && styles.end,
              )}
            >
              <b>{m.title}</b>
              {m.sub && <span className={styles.sub}>{m.sub}</span>}
              {m.days && (
                <span className={styles.days} style={{ color: MARK[m.kind ?? ''] }}>
                  {m.days}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      {legend.length > 0 && (
        <div className={styles.legend}>
          {legend.map(([k, label, bg]) => (
            <span key={k}>
              <i className={styles.swatch} style={{ background: bg }} aria-hidden="true" />
              {label}
            </span>
          ))}
        </div>
      )}
      <ul className={mergeClasses(styles.list, crowded && styles.listShown)}>
        {lines.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
    </div>
  )
}
